/* ============================================================
   Quote Basket — Store Fittings Direct
   Manages the quote basket in localStorage and drives the
   quote drawer UI. Mirrors the native cart drawer behaviour.
   On submission, requires Shopify customer auth then posts
   to the VPS backend to create a draft order.
   ============================================================ */

const QUOTE_STORAGE_KEY = 'sfd_quote_basket';
const QUOTE_BACKEND_URL = 'https://quote.storetecdirect.co.uk/quote/submit';

// ── Storage helpers ──────────────────────────────────────────

function getQuote() {
  try {
    return JSON.parse(localStorage.getItem(QUOTE_STORAGE_KEY)) || [];
  } catch {
    return [];
  }
}

function saveQuote(items) {
  localStorage.setItem(QUOTE_STORAGE_KEY, JSON.stringify(items));
  updateQuoteCount();
}

function clearQuote() {
  localStorage.removeItem(QUOTE_STORAGE_KEY);
  updateQuoteCount();
}

// ── Add to quote ─────────────────────────────────────────────

function addToQuote(variantId, quantity, productTitle, variantTitle, price, image, productUrl, customOptions) {
  const items = getQuote();
  const key = String(variantId);
  const existing = items.find(i => String(i.variantId) === key);

  if (existing) {
    existing.quantity += quantity;
    if (customOptions) existing.customOptions = customOptions;
  } else {
    items.push({
      variantId: key,
      quantity,
      productTitle,
      variantTitle,
      price,
      image,
      productUrl,
      customOptions: customOptions || null
    });
  }

  saveQuote(items);
  renderQuoteDrawer();
  openQuoteDrawer();
  showAddedToast(productTitle);
}

function addToQuoteItem(item) {
  const items = getQuote();
  const key = String(item.variantId);
  const existing = items.find(i => String(i.variantId) === key);
  if (existing) {
    existing.quantity += item.quantity;
    if (item.customOptions) existing.customOptions = item.customOptions;
  } else {
    items.push({ ...item, variantId: key });
  }
  saveQuote(items);
}

// ── Remove / update ──────────────────────────────────────────

function removeFromQuote(variantId) {
  const items = getQuote().filter(i => String(i.variantId) !== String(variantId));
  saveQuote(items);
  renderQuoteDrawer();
}

function updateQuoteQuantity(variantId, quantity) {
  const items = getQuote();
  const item = items.find(i => String(i.variantId) === String(variantId));
  if (!item) return;
  if (quantity <= 0) { removeFromQuote(variantId); return; }
  item.quantity = quantity;
  saveQuote(items);
  renderQuoteDrawer();
}

// ── Count badge ──────────────────────────────────────────────

function updateQuoteCount() {
  const items = getQuote();
  const total = items.reduce((sum, i) => sum + i.quantity, 0);
  document.querySelectorAll('[data-quote-count]').forEach(el => {
    el.textContent = total > 99 ? '99+' : total;
    el.hidden = total === 0;
  });
}

// ── Format price ─────────────────────────────────────────────

function formatPrice(pence) {
  const amount = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
  return amount + ' GBP';
}

// ── Render drawer ────────────────────────────────────────────

function renderQuoteDrawer() {
  const items = getQuote();
  const drawer = document.getElementById('QuoteDrawer');
  if (!drawer) return;

  const emptyEl  = drawer.querySelector('[data-quote-empty]');
  const bodyEl   = drawer.querySelector('[data-quote-body]');
  const footerEl = drawer.querySelector('[data-quote-footer]');
  const listEl   = drawer.querySelector('[data-quote-list]');

  if (items.length === 0) {
    if (emptyEl)  emptyEl.hidden = false;
    if (bodyEl)   bodyEl.hidden = true;
    if (footerEl) footerEl.hidden = true;
    if (listEl)   listEl.innerHTML = '';
    return;
  }

  if (emptyEl)  emptyEl.hidden = true;
  if (bodyEl)   bodyEl.hidden = false;
  if (footerEl) footerEl.hidden = false;
  if (!listEl)  return;

  listEl.innerHTML = items.map(item => `
    <li class="cart-item flex flex-col" data-variant-id="${item.variantId}">
      <div class="cart-item__product flex items-start gap-3">
        ${item.image ? `
          <a class="cart-item__media blocks-radius media-wrapper" href="${item.productUrl}" tabindex="-1">
            <img src="${item.image}" alt="${item.productTitle}" width="100" height="100" loading="lazy" style="object-fit:cover;width:100%;height:100%;">
          </a>` : ''}
        <div class="cart-item__details flex-grow flex flex-col gap-3">
          <div class="flex justify-between gap-3">
            <div class="grid flex-1">
              <div class="block">
                <a href="${item.productUrl}" class="cart-item__title text-pcard-title reversed-link">${item.productTitle}</a>
              </div>
              ${item.variantTitle && item.variantTitle !== 'Default Title' ? `
                <div class="cart-item__options">
                  <span class="cart-item__option-value text-sm text-subtext">${item.variantTitle}</span>
                </div>` : ''}
              ${item.customOptions ? `
                <div class="cart-item__options">
                  ${Object.entries(item.customOptions)
                    .filter(([key]) => !key.startsWith('_'))
                    .map(([key, val]) => `<span class="cart-item__option-value text-sm text-subtext">${key}: ${val}</span>`)
                    .join('')}
                </div>` : ''}
            </div>
            <button
              type="button"
              class="cart-item__remove flex items-center justify-center relative btn-remove"
              aria-label="Remove ${item.productTitle}"
              onclick="removeFromQuote('${item.variantId}')"
            >
              <span class="flex">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </span>
            </button>
          </div>
          <div class="cart-item__action flex items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <quantity-input class="cart-quantity quantity self-end">
                <button type="button" name="minus" class="quantity__button" aria-label="Decrease quantity"
                  onclick="updateQuoteQuantity('${item.variantId}', ${item.quantity - 1})">
                  <svg width="16" height="16" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.6875 7H12.3125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <input class="quantity__input" type="number" value="${item.quantity}" min="1" size="2" inputmode="numeric" autocomplete="off"
                  onchange="updateQuoteQuantity('${item.variantId}', parseInt(this.value) || 1)">
                <button type="button" name="plus" class="quantity__button" aria-label="Increase quantity"
                  onclick="updateQuoteQuantity('${item.variantId}', ${item.quantity + 1})">
                  <svg width="16" height="16" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.6875 7H12.3125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M7.5 2.1875V11.8125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </quantity-input>
            </div>
            <div class="cart-item__prices text-right">
              <div class="price font-body-bolder">${formatPrice(item.price * item.quantity)}</div>
              ${item.quantity > 1 ? `<div class="text-sm text-subtext">${formatPrice(item.price)} each</div>` : ''}
            </div>
          </div>
        </div>
      </div>
    </li>
  `).join('');

  const subtotal = items.reduce((sum, i) => sum + (i.price * i.quantity), 0);
  const subtotalEl = drawer.querySelector('[data-quote-subtotal]');
  if (subtotalEl) subtotalEl.textContent = formatPrice(subtotal);
}

// ── Drawer open / close ──────────────────────────────────────

function openQuoteDrawer() {
  const drawer = document.getElementById('QuoteDrawer');
  if (!drawer) return;
  drawer.removeAttribute('hidden');
  document.body.classList.add('overflow-hidden');
  requestAnimationFrame(() => drawer.setAttribute('active', ''));
}

function closeQuoteDrawer() {
  const drawer = document.getElementById('QuoteDrawer');
  if (!drawer) return;
  drawer.removeAttribute('active');
  document.body.classList.remove('overflow-hidden');
  setTimeout(() => drawer.setAttribute('hidden', ''), 300);
}

// ── Toast ────────────────────────────────────────────────────

function showAddedToast(productTitle) {
  let toast = document.getElementById('QuoteToast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'QuoteToast';
    toast.style.cssText = `
      position:fixed;bottom:2rem;left:50%;transform:translateX(-50%);
      background:rgb(var(--color-foreground));color:rgb(var(--color-background));
      padding:1rem 2rem;border-radius:var(--buttons-radius);
      font-size:1.4rem;z-index:9999;opacity:0;
      transition:opacity 0.3s ease;pointer-events:none;white-space:nowrap;
    `;
    document.body.appendChild(toast);
  }
  toast.textContent = `"${productTitle}" added to quote`;
  toast.style.opacity = '1';
  clearTimeout(toast._timeout);
  toast._timeout = setTimeout(() => { toast.style.opacity = '0'; }, 2500);
}

// ── Submit quote flow ────────────────────────────────────────

function initiateQuoteSubmit() {
  const isLoggedIn = document.body.classList.contains('customer-logged-in') ||
                     window.__sfd_customer_logged_in === true;

  if (!isLoggedIn) {
    closeQuoteDrawer();
    setTimeout(() => openAuthModal(), 350);
    return;
  }

  const customerName  = window.__sfd_customer_name || '';
  const customerPhone = window.__sfd_customer_phone || '';
  const needsDetails  = !customerName.trim() || !customerPhone.trim();

  if (needsDetails) {
    openDetailsModal();
  } else {
    submitQuoteToServer();
  }
}

// ── Details modal ────────────────────────────────────────────

function openAuthModal() {
  const modal = document.getElementById('QuoteAuthModal');
  if (modal) {
    var returnUrl = encodeURIComponent(window.location.pathname + '?quote_submit=1');
    var container = document.getElementById('quote-signin-container');
    if (container) {
      container.innerHTML = '<a href="/account/login?return_url=' + returnUrl + '" class="btn btn--secondary w-full" style="text-align:center;display:block;"><span class="btn__text">Sign in to my account</span></a>' +
        '<p style="font-size:1.3rem;color:rgb(var(--color-button-text));opacity:0.75;margin-top:1.6rem;text-align:center;">Don\'t have an account? <a href="/account/register?return_url=' + returnUrl + '" style="text-decoration:underline;">Create one</a></p>';
    }
    modal.removeAttribute('hidden');
    requestAnimationFrame(() => modal.setAttribute('active', ''));
  }
}

function closeAuthModal() {
  const modal = document.getElementById('QuoteAuthModal');
  if (modal) {
    modal.removeAttribute('active');
    setTimeout(() => modal.setAttribute('hidden', ''), 300);
  }
}

async function submitGuestQuote(event) {
  event.preventDefault();
  const form      = document.getElementById('QuoteGuestForm');
  const btn       = form.querySelector('[data-guest-btn]');
  const errEl     = form.querySelector('[data-guest-error]');
  const firstName = form.querySelector('[name="firstName"]').value.trim();
  const lastName  = form.querySelector('[name="lastName"]').value.trim();
  const email     = form.querySelector('[name="email"]').value.trim();
  const phone     = form.querySelector('[name="phone"]').value.trim();

  if (!firstName || !lastName || !email || !phone) {
    errEl.textContent = 'Please fill in all fields.';
    errEl.hidden = false;
    return;
  }

  btn.disabled = true;
  btn.querySelector('.btn__text').textContent = 'Sending…';
  errEl.hidden = true;

  closeAuthModal();
  submitQuoteToServer({ firstName, lastName, email, phone, isGuest: true }).catch(() => {
    btn.disabled = false;
    btn.querySelector('.btn__text').textContent = 'Get My Quote';
  });
}

function openDetailsModal() {
  closeQuoteDrawer();
  setTimeout(() => {
    const modal = document.getElementById('QuoteDetailsModal');
    if (modal) {
      modal.removeAttribute('hidden');
      requestAnimationFrame(() => modal.setAttribute('active', ''));
    }
  }, 350);
}

function closeDetailsModal() {
  const modal = document.getElementById('QuoteDetailsModal');
  if (modal) {
    modal.removeAttribute('active');
    setTimeout(() => modal.setAttribute('hidden', ''), 300);
  }
}

async function saveDetailsAndSubmit(event) {
  event.preventDefault();
  const form      = document.getElementById('QuoteDetailsForm');
  const btn       = form.querySelector('[data-submit-btn]');
  const errEl     = form.querySelector('[data-form-error]');
  const firstName = form.querySelector('[name="firstName"]').value.trim();
  const lastName  = form.querySelector('[name="lastName"]').value.trim();
  const phone     = form.querySelector('[name="phone"]').value.trim();

  if (!firstName || !lastName || !phone) {
    errEl.textContent = 'Please fill in all fields.';
    errEl.hidden = false;
    return;
  }

  btn.disabled = true;
  btn.querySelector('.btn__text').textContent = 'Saving…';
  errEl.hidden = true;

  window.__sfd_customer_name  = `${firstName} ${lastName}`;
  window.__sfd_customer_phone = phone;

  closeDetailsModal();
  submitQuoteToServer({ firstName, lastName, phone });
}

// ── Server submission ────────────────────────────────────────

async function submitQuoteToServer(extraDetails) {
  closeQuoteDrawer();
  showSubmittingOverlay();

  const items = getQuote();
  if (items.length === 0) { hideSubmittingOverlay(); return; }

  const payload = {
    lineItems: items,
    customer: {
      id:        extraDetails?.isGuest ? null : (window.__sfd_customer_id || null),
      email:     extraDetails?.email   || window.__sfd_customer_email     || null,
      firstName: extraDetails?.firstName || window.__sfd_customer_first_name || '',
      lastName:  extraDetails?.lastName  || window.__sfd_customer_last_name  || '',
      phone:     extraDetails?.phone     || window.__sfd_customer_phone      || ''
    }
  };

  try {
    const res    = await fetch(QUOTE_BACKEND_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload)
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Submission failed');

    clearQuote();
    renderQuoteDrawer();
    setTimeout(() => {
      hideSubmittingOverlay();
      window.location.href = 'https://account.storefittingsdirect.co.uk/pages/019d21a4-c263-736d-81ea-741e7b12e02a';
    }, 4000);

  } catch (err) {
    hideSubmittingOverlay();
    showErrorOverlay(err.message);
  }
}

// ── Overlays ─────────────────────────────────────────────────

function showSubmittingOverlay() {
  let el = document.getElementById('QuoteSubmitting');
  if (!el) {
    el = document.createElement('div');
    el.id = 'QuoteSubmitting';
    el.style.cssText = `position:fixed;inset:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:9999;`;
    el.innerHTML = `<div style="background:rgb(var(--color-background));padding:3rem;border-radius:1.6rem;text-align:center;max-width:40rem;margin:2rem;">
      <svg style="width:3.2rem;height:3.2rem;margin:0 auto 1.6rem;display:block;animation:sfd-spin 1s linear infinite;" viewBox="0 0 66 66">
        <circle fill="none" stroke="currentColor" stroke-width="4" cx="33" cy="33" r="30"/>
      </svg>
      <p style="font-size:1.6rem;font-weight:600;margin-bottom:0.8rem;">We are emailing you your quote</p>
      <p style="font-size:1.4rem;color:rgb(var(--color-foreground-muted));margin-bottom:2rem;">You should receive this in the next minute or so. If you have any issues, please log into your account where you'll find all of your quotes.</p>
      <p style="font-size:1.4rem;color:rgb(var(--color-foreground-muted));">If you need any further assistance, please call us on <strong>0116 318 4182</strong></p>
    </div>`;
    document.body.appendChild(el);
    const s = document.createElement('style');
    s.textContent = '@keyframes sfd-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(s);
  }
  el.style.display = 'flex';
}

function hideSubmittingOverlay() {
  const el = document.getElementById('QuoteSubmitting');
  if (el) el.style.display = 'none';
}

function closeErrorAndRetry() {
  document.getElementById('QuoteError').style.display = 'none';
  var b = document.querySelector('#QuoteGuestForm [data-guest-btn]');
  if (b) {
    b.disabled = false;
    b.querySelector('.btn__text').textContent = 'Get My Quote';
  }
  openAuthModal();
}

function showErrorOverlay(message) {
  let el = document.getElementById('QuoteError');
  if (!el) {
    el = document.createElement('div');
    el.id = 'QuoteError';
    el.style.cssText = `position:fixed;inset:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:9999;`;
    el.innerHTML = `<div style="background:rgb(var(--color-background));padding:3rem;border-radius:1.6rem;max-width:44rem;text-align:center;">
      <p style="margin-bottom:1.6rem;font-size:1.5rem;" id="QuoteErrorMsg"></p>
      <button class="btn btn--primary" onclick="closeErrorAndRetry()">
        <span class="btn__text">Try again</span>
      </button>
    </div>`;
    document.body.appendChild(el);
  }
  document.getElementById('QuoteErrorMsg').textContent = 'Something went wrong. Please try again or call us on 0116 318 4182.';
  el.style.display = 'flex';
}

// ── Auto-submit after login return ───────────────────────────

function checkPostLoginReturn() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('quote_submit') === '1' && getQuote().length > 0) {
    history.replaceState({}, '', window.location.pathname);
    setTimeout(initiateQuoteSubmit, 500);
  }
}

// ── Initialise ───────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  updateQuoteCount();
  renderQuoteDrawer();
  checkPostLoginReturn();

    document.addEventListener('click', e => {
    const drawer = document.getElementById('QuoteDrawer');
    if (drawer && drawer.hasAttribute('active')) {
      const inner = drawer.querySelector('.drawer__inner');
      if (inner && !inner.contains(e.target) && document.body.contains(e.target)) {
        closeQuoteDrawer();
      }
    }
    if (e.target.classList.contains('fixed-overlay') && e.target.closest('#QuoteDetailsModal')) closeDetailsModal();
    if (e.target.classList.contains('fixed-overlay') && e.target.closest('#QuoteAuthModal')) closeAuthModal();
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') { closeQuoteDrawer(); closeDetailsModal(); closeAuthModal(); }
  });
});
